// REACT ====================================================================================
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// UUID =====================================================================================
import { v4 as uuid } from 'uuid';
// ICONS ====================================================================================
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// MATERIAL UI ==============================================================================
import { Button } from '@mui/material';

// DATA =====================================================================================
import BlockData from '../../../models/BlockData';
import BlockProps from '../../../models/BlockProps';
import BlockCarouselData from './BlockCarouselData';
import { BlockHeight } from '../../../models/BlockSettings';
import CarouselModal from '../../Modals/CarouselModal/CarouselModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';

// STYLES ===================================================================================
import styles from './BlockCarouselWithPureJs.module.css';

// COMPONENTS ===============================================================================
import FadeCarousel from './components/FadeCarousel';
import CarouselItem from './components/CarouselItem';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';

// HOOKS ====================================================================================
import useGetBackground from '../../../hooks/UseGetBackground';
import useParallaxScript from '../../../hooks/useParallaxScript';

// UTILS ====================================================================================
import config from '../../../config';

// INTERFACES ===============================================================================
export interface BlockCarouselWithPureJsProps extends BlockProps {
  blockData: BlockData[]
}

// CONSTS ===================================================================================
const DefaultAutoInterval = 6000;

// MAIN COMPONENT ===========================================================================
function BlockCarouselWithPureJs({
  data,
  blockData,
  editHandler,
  isEditing,
  isTemplate,
  showImageModal,
  onCloseImageModal,
}: BlockCarouselWithPureJsProps) {
  // STATES =================================================================================
  const [height, setHeight] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [props, setProps] = useState<BlockCarouselData>(data.data as BlockCarouselData);

  // COMPONENT CONSTS =======================================================================
  const elementUuid = uuid();
  const first = props.carousel[0];
  const last = props.carousel[props.carousel.length - 1];

  const uiCarouselData = [{
    id: 'last-copy',
    image: last.image,
    link: last.link,
  }, ...props.carousel, {
    id: 'first-copy',
    image: first.image,
    link: first.link,
  }];

  const rightBoundary = -uiCarouselData.length * 100 + 100;

  // HOOKS ===================================================================================
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  // REFS ===================================================================================
  const sliderRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLDivElement>(null);
  const leftNaviButton = useRef<HTMLButtonElement>(null);
  const rightNaviButton = useRef<HTMLButtonElement>(null);

  const isDragingRef = useRef<boolean>(false);
  const basePositionRef = useRef<number>(0);
  const baseTranslateX = useRef<number>(0);

  // MEMOS ===================================================================================
  const interval = useMemo(() => (props.autoInterval != null ? props.autoInterval * 1000 : DefaultAutoInterval), [props.autoInterval]);
  const mode = useMemo(() => (props.mode != null ? props.mode : 'slide'), [props.mode]);

  // CALLBACKS ===============================================================================
  const getCurrentTranslate = useCallback((): number => {
    const translate = slidesRef.current.style.transform;
    const regex = /(-?[\d\\.]+)%/;
    const match = translate.match(regex);
    const currentTranslateX = parseInt(match[1], 10); // "-100%"
    return currentTranslateX;
  }, []);

  const handleRewind = useCallback((offset: number) => {
    if (offset === 0) {
      // reach the the left, jump to the last slide with the animation in 0s
      setTimeout(() => {
        slidesRef.current.style.transition = 'all 0s ease-in-out';
        slidesRef.current.style.transform = `translate(${-(uiCarouselData.length - 2) * 100}%)`;
      }, 600);
    }

    if (offset === rightBoundary) {
      // reach the right, jump to the first slide with the animation in 0s
      setTimeout(() => {
        slidesRef.current.style.transition = 'all 0s ease-in-out';
        slidesRef.current.style.transform = 'translate(-100%)';
      }, 600);
    }
  }, [rightBoundary, uiCarouselData.length]);

  const goToPrevious = (currentTranslateX: number) => {
    let previous = currentTranslateX;

    if (previous % 100 === 0) {
      previous += 100;
    } else {
      previous -= previous % 100;
    }

    handleRewind(previous);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${previous}%)`;
  };

  const goToNext = useCallback((currentTranslateX: number) => {
    let next = currentTranslateX;
    if (next % 100 === 0) {
      next -= 100;
    } else {
      next -= next % 100;
      next -= 100;
    }

    handleRewind(next);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${next}%)`;
  }, [handleRewind]);

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpenModal(true);
    }
  }, [isEditing, isTemplate]);

  const handleClose = () => setOpenModal(false);

  const getDirection = (baseClientX: number, clientX: number): 'left' | 'right' => {
    if (clientX < baseClientX) {
      return 'right';
    }
    return 'left';
  };

  const remainPrevious = (currentTranslateX: number) => {
    if (currentTranslateX % 100 === 0) {
      return;
    }

    const v = currentTranslateX - (currentTranslateX % 100);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${v}%)`;
  };

  const remainNext = (currentTranslateX: number) => {
    if (currentTranslateX % 100 === 0) {
      return;
    }

    const v = currentTranslateX - (currentTranslateX % 100) - 100;
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${v}%)`;
  };

  const handleStart = (currentClientX: number) => {
    isDragingRef.current = true;
    basePositionRef.current = currentClientX;
    baseTranslateX.current = getCurrentTranslate();
  };

  const handleMove = (currentClientX: number) => {
    if (isDragingRef.current === false) {
      return;
    }
    const nextTranslateX = baseTranslateX.current + (100 * (currentClientX - basePositionRef.current)) / sliderRef.current.clientWidth;
    slidesRef.current.style.transform = `translateX(${nextTranslateX}%)`;
    slidesRef.current.style.transition = 'all 0s ease-in-out';
  };

  const handleEnd = (currentClientX: number) => {
    if (isDragingRef.current === false) {
      return;
    }
    isDragingRef.current = false;
    const currentTranslateX = getCurrentTranslate();

    if (getDirection(basePositionRef.current, currentClientX) === 'right') {
      if (currentTranslateX % 100 <= -20) {
        goToNext(currentTranslateX);
      } else {
        remainPrevious(currentTranslateX);
      }
    }

    if (getDirection(basePositionRef.current, currentClientX) === 'left') {
      if (currentTranslateX % 100 >= -80) {
        goToPrevious(currentTranslateX);
      } else {
        remainNext(currentTranslateX);
      }
    }

    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
  };

  // EFFECTS ================================================================================
  useEffect(() => {
    setProps(data.data as BlockCarouselData);
  }, [data.data]);

  useEffect(() => {
    if (props.height === BlockHeight.TALL) {
      setHeight('650px');
    } else {
      setHeight('350px');
    }
  }, [props.height]);

  useEffect(() => {
    if (sliderRef.current == null) {
      return () => { };
    }
    sliderRef.current.setAttribute('auto', `${props.auto}`);
    sliderRef.current.setAttribute('auto_interval', `${interval}`);
    if (props.auto === false) {
      return () => { };
    }

    const id = setInterval(() => {
      goToNext(getCurrentTranslate());
    }, interval);

    return () => {
      clearInterval(id);
    };
  }, [props.auto, interval, goToNext, getCurrentTranslate]);

  // RENDER =================================================================================
  return (
    <>
      <div
        className={mode === 'slide' ? 'blockCarouselSliderRoot' : 'blockCarouselSliderRootFade'}
        id={elementUuid}
        style={{
          overflow: 'hidden',
          ...useGetBackground(
            props.url?.solidColor,
            props.url?.value,
            props.url?.settings?.scale,
            props.url?.settings?.scaleBool,
          ),
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={props.url}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, url: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        )}
        {mode === 'slide' && (
          <>
            <div
              ref={sliderRef}
              role="presentation"
              id="blockCarouselSlider"
              className={[styles.slider, 'blockCarouselSlider'].join(' ')}
              onTouchStart={(e) => {
                handleStart(e.touches[0].clientX);
              }}
              onTouchMove={(e) => {
                handleMove(e.touches[0].clientX);
              }}
              onTouchEnd={(e) => {
                handleEnd(e.changedTouches[0].clientX);
              }}
              onMouseDown={(e) => {
                handleStart(e.clientX);
              }}
              onMouseMove={(e) => {
                handleMove(e.clientX);
              }}
              onMouseUp={(e) => {
                handleEnd(e.clientX);
              }}
              onMouseLeave={(e) => {
                handleEnd(e.clientX);
              }}
            >
              <div id="blockCarouselSlides" ref={slidesRef} className={[styles.slides, 'blockCarouselSlides'].join(' ')} style={{ transform: 'translateX(-100%)' }}>
                {uiCarouselData.map((item) => (
                  <div
                    className={styles.slidesContainer}
                    key={item.id}
                    id={`slide-${item.id}`}
                    style={{
                      height: '100%',
                      maxHeight: !props.fixedAspectRatio ? height : undefined,
                    }}
                  >
                    <CarouselItem
                      item={item}
                      isEditing={isEditing}
                      handleOpen={handleOpen}
                      blockHeight={props.height}
                      maxHeight={(props.height === BlockHeight.TALL ? 650 : 350)}
                      useFixedAspectRatio={props.fixedAspectRatio}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button ref={leftNaviButton} startIcon={<KeyboardArrowLeftIcon />} id="blockCarouselLeftNavi" type="button" onClick={() => goToPrevious(getCurrentTranslate())} className={[styles.leftNaviButton, 'blockCarouselLeftNavi'].join(' ')} />
            <Button ref={rightNaviButton} startIcon={<KeyboardArrowRightIcon />} id="blockCarouselRightNavi" type="button" onClick={() => goToNext(getCurrentTranslate())} className={[styles.rightNaviButton, 'blockCarouselRightNavi'].join(' ')} />
          </>
        )}

        {mode === 'fade' && (
          <FadeCarousel
            carouselData={props.carousel}
            height={props.height}
            isEditing={isEditing}
            handleOpen={handleOpen}
            auto={props.auto}
            interval={interval}
            useFixedAspectRatio={props.fixedAspectRatio}
            maxHeight={(props.height === BlockHeight.TALL ? 650 : 350)}
          />
        )}
      </div>

      <CarouselModal
        open={openModal}
        mode={props.mode}
        auto={props.auto}
        carousel={props.carousel}
        blockHeight={props.height}
        fixedAspectRatio={props.fixedAspectRatio}
        slideId="slideId"
        autoInterval={props.autoInterval || 1}
        blockData={data}
        handleClose={handleClose}
        handleSave={(newItems, newAuto, autoInterval, mode) => {
          props.auto = newAuto;
          props.autoInterval = autoInterval;
          props.carousel = newItems;
          props.mode = mode;
          editHandler(data.id, props);
          handleClose();
        }}
      />
    </>
  );
}

export default BlockCarouselWithPureJs;
